/**
 * [PROGRAM] system-prompt block for an onboarding chat that started on a
 * program lander (LEV-2359, flag `accelerators-straight-in`). `deckShared`
 * (resolved by the caller from the materials slot status) switches in a
 * DECK FAST PATH sub-block that overrides the normal interview down to two
 * questions — the founder ask and program confirmation — for a founder who
 * arrived with a deck already in hand. `confirmOnCanvas` (flag
 * `accelerators-row`) moves program confirmation onto the founder's canvas:
 * the accelerator question goes, the fast path asks only the founder ask, a
 * shared website (`siteShared`) takes that fast path too, and a founder with
 * nothing shared gets the NO MATERIALS FAST PATH instead of the full
 * interview. Off, the block is byte-identical to the straight-in-only block
 * and `siteShared` is ignored. A premier program has no confirm step on the
 * canvas (programFirstStop opens its application), so its wrap-up promises
 * the application instead; a landing-tier program still confirms.
 *
 * INVARIANT: takes a resolved ProgramSlug and renders ONLY registry strings
 * (src/constants/programs.ts). The raw request value never reaches the
 * prompt; callers resolve it with resolveProgramSlug first.
 *
 * The block holds no [MATERIALS_*] tokens so [MATERIALS_READ_DUE] stays the
 * last materials directive the model reads.
 */
import { PROGRAMS, type ProgramOffers, type ProgramSlug } from "@/constants/programs";
import { programOffers } from "@/lib/accelerators/applicationStage";
import { isPremierProgramFormat } from "@/lib/accelerators/firstStop";

/**
 * The one distinct id `accelerators-row` is read for before signup, by the
 * lander page and by the chat route alike. The client acts on the page's
 * verdict (the fixed first reply, the read follow-up) and the route shapes
 * the prompt from its own, so a per-visitor read in either place lets a
 * targeted or percentage rollout split the client from the prompt.
 */
export const PROGRAM_LANDER_ROW_FLAG_ID = "anonymous";

/**
 * What the wrap-up promises beside the draft (", plus a team video script and
 * interview prep."): only what the program's flow produces (programOffers),
 * the same source the lander and hand-off read.
 */
function wrapUpExtras(offers: ProgramOffers): string {
  const extras = [offers.video ? `a ${offers.video} video script` : null, offers.interviewPrep ? "interview prep" : null];
  const named = extras.filter((extra): extra is string => extra !== null);
  return named.length > 0 ? `, plus ${named.join(" and ")}.` : ".";
}

/**
 * The canvas-first wrap-up lines. A premier program has no confirm step (the
 * founder lands on its application, already drafting), so only a landing-tier
 * program may promise one.
 */
export function confirmOnCanvasWrapUpLines(name: string, premier: boolean, offers: ProgramOffers): string[] {
  const extras = wrapUpExtras(offers);
  return premier
    ? [
        `- Never ask which ${name} accelerator, location or track they want: Lev picks ${name} for them on their canvas.`,
        `- At wrap-up, tell them their canvas opens next on their ${name} application, and Lev starts drafting it right away${extras}`,
      ]
    : [
        `- Never ask which ${name} accelerator, location or track they want: they confirm their program on their canvas right after signup.`,
        `- At wrap-up, tell them their canvas opens next with ${name} ready to confirm, and Lev drafts their ${name} application from there${extras}`,
      ];
}

export function buildProgramContext(
  slug: ProgramSlug,
  options: { deckShared?: boolean; siteShared?: boolean; confirmOnCanvas?: boolean } = {},
): string {
  const program = PROGRAMS[slug];
  const { name } = program;
  const offers = programOffers(program.appFormatId);
  const optionsLine = program.acceleratorOptions?.length
    ? ` Options: ${program.acceleratorOptions.join(", ")}.`
    : "";
  const acceleratorQuestion = `Which ${name} accelerator are you aiming for?`;
  // The deckShared variant can flip mid-chat (a deck lands after a pasted
  // URL), moving the question between branches; both carry this so it is
  // never asked twice.
  const skipIfAnswered = "Skip it if the conversation already answered it.";
  const lines = [
    `[PROGRAM name="${name}" cohort="${program.cohortLabel}" deadline="${program.deadlineLabel}"]`,
    `The founder is applying to ${name} ${program.cohortLabel}, closing ${program.deadlineLabel}. Run the normal interview with these changes:`,
    `- The page already asked for their deck (an upload chip, before the chat opened). Do NOT ask for it again; wait for them to share it or say they don't have one.`,
    `- Materials first: if they have not shared a deck, document or website yet, ask for one ONCE, early, then carry on without it.`,
    `- The page's upload chip also invites their current ${name} application, not only a pitch deck. If what they shared reads as application answers (questions and answers in an application's own format) rather than a pitch deck, call it their application, not a deck, and never list missing slides as a gap.`,
    `- Never ask the RAISING INTENT question (category 3) — this founder's raisingIntent is seeded as "${program.seed?.raisingIntent ?? "planning"}" unless they say otherwise, in which case record what they actually said.`,
    `- After you have delivered your read of their materials (or once it is clear there are none), ask two team questions, each on its own turn: (1) who is on the team and how long they have worked together; (2) why this team is the right one for this idea (founder-idea fit). Skip either one the conversation already answered.`,
    ...(options.confirmOnCanvas
      ? [
          ...confirmOnCanvasWrapUpLines(name, isPremierProgramFormat(program.appFormatId), offers),
          `- Never write an em-dash: use a comma, a colon or a period instead.`,
        ]
      : [
          `- At wrap-up, tell them Lev will draft their ${name} application next${wrapUpExtras(offers)}`,
        ]),
    `- Never imply ${name} endorses, partners with or reviews applications through Lev, and never predict whether they will be admitted.`,
  ];
  const siteFastPath = options.confirmOnCanvas === true && options.siteShared === true;
  if (options.deckShared || siteFastPath) {
    // INVARIANT (same reason as the module header): this block names no
    // [MATERIALS_*] token literally, only describes the parse state in prose
    // — a literal token here would make [MATERIALS_READ_DUE] no longer the
    // last materials directive the model reads, and could be mistaken for a
    // real gate by anything that scans the prompt text for that marker.
    lines.push(
      ...(options.confirmOnCanvas
        ? canvasConfirmFastPath(sharedMaterialsNoun(options.deckShared === true, siteFastPath))
        : [
            `[DECK FAST PATH]`,
            `The founder already shared a deck or document — a fast path replaces the CONVERSATION SEQUENCE (categories 1-5), the DILIGENCE section, and the two team questions above. The "never a shorter conversation" rule does not apply here; the HARD GATES in the base prompt (identity, materials) still bind. Ask exactly two things, each on its own turn, then conclude:`,
            `1. The FOUNDER ask (category 6): their background link (LinkedIn, resume, or personal site).${BACKGROUND_LINK_REASON}`,
            `2. Program confirmation, once you have asked (1): "You're applying to ${name} ${program.cohortLabel}, closing ${program.deadlineLabel}. ${acceleratorQuestion}"${optionsLine} Record their answer in result.notes. ${skipIfAnswered}`,
            `If both (1) and (2) are answered and the deck or site analysis is still running, ask exactly ONE question grounded in the deck (a gap or a DILIGENCE probe on a stated claim) before concluding. Deliver your read of the materials in the SAME message where you conclude — do not add a separate turn for it.`,
            `[/DECK FAST PATH]`,
          ]),
    );
  } else if (options.confirmOnCanvas) {
    lines.push(...noMaterialsFastPath());
  } else {
    // A pasted URL (or no materials at all) never reaches the DECK FAST PATH
    // above, so the normal interview must ask this itself, once, on its own
    // turn, alongside the two team questions above.
    lines.push(
      `- Also ask, on its own turn: "${acceleratorQuestion}"${optionsLine} Record their answer in result.notes. ${skipIfAnswered}`,
    );
  }
  lines.push(`[/PROGRAM]`);
  return lines.join("\n");
}

/**
 * Why Lev wants the background link (LEV-2721), as IdeaChatInput's fixed
 * first reply also says. The reason MUST come before the question: a reply
 * that does not end on a question reads to the client as "Lev announced the
 * read" (decideMaterialsNudge), and it would follow that up in the founder's
 * name while Lev still waits for the link.
 */
const BACKGROUND_LINK_REASON =
  " When you ask, say first that it helps you write their team answers, and end the message on the question.";

/**
 * DECK FAST PATH when the program is confirmed on the canvas: the background
 * link is the only ask, and a read still running is announced, not filled
 * with a question. That announcement must not end in a question: the client
 * follows it up itself once the parse lands (IdeaChatInput's
 * decideMaterialsNudge), and a question would hand the floor to the founder
 * and suppress that follow-up.
 *
 * INVARIANT: IdeaChatInput's fixed first reply to a dropped deck
 * (PROGRAM_DECK_FIRST_REPLY) asks for the background link without a model
 * call, so an earlier ask MUST read as already made, or the founder is asked
 * twice.
 */
function canvasConfirmFastPath(noun: SharedMaterialsNoun): string[] {
  const shared = noun === "deck" ? "a deck or document" : `their ${noun}`;
  return [
    `[DECK FAST PATH]`,
    `The founder already shared ${shared}. A fast path replaces the CONVERSATION SEQUENCE (categories 1-5), the DILIGENCE section, and the two team questions above. The "never a shorter conversation" rule does not apply here; the HARD GATES in the base prompt (identity, materials) still bind. Ask exactly ONE thing, then conclude:`,
    `1. The FOUNDER ask (category 6): their background link (LinkedIn, resume, or personal site).${BACKGROUND_LINK_REASON} If an earlier message already asked for it, do not ask again: the founder's next message is the answer.`,
    `Once (1) is answered: if the deck or site analysis is still running, reply in ONE short line saying you're finishing your read of their ${noun}, and ask nothing new. Do not repeat their link back to them. This replaces the materials gate's deepening-question rule for this founder. Keep result.status "partial" on that turn; your read arrives in your next message. Once the analysis has landed, deliver your read of the materials and conclude in the SAME message.`,
    `[/DECK FAST PATH]`,
  ];
}

type SharedMaterialsNoun = "deck" | "website" | "deck and website";

function sharedMaterialsNoun(deck: boolean, site: boolean): SharedMaterialsNoun {
  if (deck && site) return "deck and website";
  return site ? "website" : "deck";
}

/**
 * Program confirmed on the canvas and nothing to read (no deck or site, or
 * its read failed): the Big Idea needs problem, customer and solution, and
 * the IDENTITY gate needs the background link, so that is all this asks.
 * Stage, traction and team are left to the canvas and the application flow.
 * A website pasted in answer flips the chat to the DECK FAST PATH next turn.
 */
function noMaterialsFastPath(): string[] {
  return [
    `[NO MATERIALS FAST PATH]`,
    `The founder has not shared a deck or website we can read. A fast path replaces the CONVERSATION SEQUENCE (categories 1-5), the DILIGENCE section, the two team questions and the materials-first ask above. The "never a shorter conversation" rule does not apply here; the HARD GATES in the base prompt still bind. Ask at most these three things, each on its own turn, skipping any the conversation already answered, then conclude:`,
    `1. In a few sentences, what they are building: the problem, who has it, and how they solve it. If they have not shared a website, add in the same message that a link to it works too.`,
    `2. Only if the problem, the customer or the solution is still unclear after (1): ONE follow-up on the vaguest of them.`,
    `3. The FOUNDER ask (category 6): their background link (LinkedIn, resume, or personal site), as the only question in its message. Do not repeat their link back to them.`,
    `Once (3) is answered, conclude in that same message.`,
    `[/NO MATERIALS FAST PATH]`,
  ];
}

/**
 * Fill-only seed: a result's raisingIntent takes the program's seed ONLY while
 * it is absent or "unknown" (the prompt instructs the model never to ask, so
 * it stays "unknown" every turn unless the founder volunteers one).
 * The seed is a known value, and mergePartialResults lets a known delta win,
 * so a caller merging into a saved row MUST check the saved value first (as
 * persistTurnToSession does) or the seed replaces the founder's own answer.
 */
export function applyProgramSeed<T extends Record<string, unknown> | null | undefined>(
  result: T,
  slug: ProgramSlug | null,
): T {
  if (!slug || !result || typeof result !== "object") return result;
  const seed = PROGRAMS[slug].seed;
  if (!seed) return result;
  const current = result.raisingIntent;
  if (current !== undefined && current !== null && current !== "unknown") return result;
  return { ...result, raisingIntent: seed.raisingIntent } as T;
}
